import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseChartDirective } from 'ng2-charts';
import { BarChartService as ChartService } from '../../../services/bar-chart.service';

type AccessibleChartRow = {
  label: string;
  values: string[];
};

@Component({
  selector: 'app-horizontal-bars-modal',
  templateUrl: './horizontal-bars-modal.component.html',
  providers: [ChartService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HorizontalBarsModalComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;

  readonly dialogRef = inject(MatDialogRef<HorizontalBarsModalComponent>);

  readonly data = inject(MAT_DIALOG_DATA);

  // #region     BEGIN ChartService
  readonly chartService$ = inject(ChartService);

  get data$() {
    return this.chartService$.data$;
  }

  readonly updateChartEffect$ = effect(() => {
    this.chartService$.barChartData$();
    this.chart?.update();
  });
  // #endregion  END ChartService

  readonly chartTitle$ = computed(() => this.data$()?.title || 'Bar chart');

  readonly accessibleChartColumns$ = computed<string[]>(() => {
    const datasets = this.chartService$.barChartData$().datasets ?? [];

    return datasets.map(
      (dataset, index) =>
        dataset.label ||
        (datasets.length === 1
          ? this.data$()?.xAxisLabel || 'Value'
          : `Series ${index + 1}`),
    );
  });

  readonly accessibleChartRows$ = computed<AccessibleChartRow[]>(() => {
    const chartData = this.chartService$.barChartData$();
    const labels = chartData.labels ?? [];
    const datasets = chartData.datasets ?? [];

    return labels.map((label, rowIndex) => ({
      label: String(label),
      values: datasets.map((dataset) => String(dataset.data?.[rowIndex] ?? '')),
    }));
  });

  readonly chartDescription$ = computed(() => {
    const title = this.chartTitle$();
    const barCount = this.accessibleChartRows$().length;
    const barLabel = barCount === 1 ? 'bar' : 'bars';

    return `${title} bar chart with ${barCount} ${barLabel}. The data table lists each label and value.`;
  });

  ngOnInit(): void {
    this.data$.set(this.data);
  }
}
